/**
 * Retrieves a localized field value from an object based on the provided base key and locale.
 *
 * The function constructs the property name by appending the locale suffix to the base key.
 * For the Swedish locale ("sv"), the suffix "SE" is used; for other locales, the locale is converted to uppercase.
 * Returns the value of the constructed property as a string, or `undefined` if the property does not exist.
 *
 * @typeParam T - The type of the object, which must be a record with string keys.
 * @param obj - The object containing localized fields.
 * @param base - The base key for the localized field.
 * @param locale - The locale code (e.g., "sv", "en").
 * @returns The localized field value as a string, or `undefined` if not found.
 */
export function getLocalizedField<T extends Record<string, unknown>>(
  obj: T,
  base: string,
  locale: string
): string | undefined {
  // Check if locale contains 'sv' (Swedish) - use SE suffix
  if (
    locale &&
    (locale.toLowerCase().includes('sv') ||
      locale === 'sv_SE' ||
      locale === 'sv-SE')
  ) {
    return obj[`${base}SE` as keyof T] as string | undefined;
  }

  // Check if locale contains 'en' (English) - use EN suffix with FI fallback
  if (
    locale &&
    (locale.toLowerCase().includes('en') ||
      locale === 'en_US' ||
      locale === 'en-US')
  ) {
    const enValue = obj[`${base}EN` as keyof T] as string | undefined;
    if (enValue && enValue.trim() !== '') return enValue;
    // Fall back to Finnish when EN field is blank/null
    return obj[`${base}FI` as keyof T] as string | undefined;
  }

  // Default to Finnish
  return obj[`${base}FI` as keyof T] as string | undefined;
}
